import { useContext, useMemo } from 'react';

import CardContext from 'contexts/CardContext';
import ComparisonContext from 'contexts/ComparisonContext';
import DashboardGadgetContext from 'contexts/DashboardGadgetContext';
import DateInputContext from 'contexts/DateInputContext';
import FilterPlatesContext from 'contexts/FilterPlatesContext';
import ReportContext from 'contexts/ReportContext';
import VariableFiltersContext from 'contexts/VariableFiltersContext';
import WeekStartsOnOverrideContext from 'contexts/WeekStartsOnOverrideContext';
import {
  GadgetCopySettings,
  quickFiltersToPlates,
  toPortablePlates,
} from 'utils/copyGadgetSettings';

/**
 * The settings a gadget is currently rendering with (scope, drill downs,
 * selected quick filters, date, comparison) plus its card size. Must be called
 * from within the gadget's card, where the report / dashboard gadget providers
 * have already merged their filters into these contexts.
 */
const useLiveGadgetCopySettings = (): {
  settings: GadgetCopySettings;
  w: number;
  h: number;
} => {
  const { w, h } = useContext(CardContext);
  const { scope, drillDowns, isDefaultFiltersDisabled } =
    useContext(FilterPlatesContext);
  const { dateField, dateRange, relativeDateRange, advancedRelativeDateRange } =
    useContext(DateInputContext);
  const { currentComparison } = useContext(ComparisonContext);
  const { weekStartsOnOverride } = useContext(WeekStartsOnOverrideContext);
  const { report, selectedQuickFilters } = useContext(ReportContext);
  const { dashboardGadget } = useContext(DashboardGadgetContext);
  const { variableFilters } = useContext(VariableFiltersContext);

  const settings = useMemo((): GadgetCopySettings => {
    const toPortable = (plates: FilterPlateType[]) =>
      toPortablePlates({ plates, variableDrillDowns: variableFilters });

    return {
      scope: toPortable(scope),
      drillDowns: toPortable([
        ...drillDowns,
        ...quickFiltersToPlates(selectedQuickFilters || []),
      ]),
      dateField: dateField || 'date',
      dateRange,
      relativeDateRange,
      advancedRelativeDateRange,
      comparison: currentComparison,
      weekStartsOnOverride,
      isDefaultFiltersDisabled: !!isDefaultFiltersDisabled,
      isEntityFilterEnabled: report
        ? report.isEntityFilterEnabled
        : dashboardGadget?.isEntityFilterEnabled,
    };
  }, [
    advancedRelativeDateRange,
    currentComparison,
    dashboardGadget?.isEntityFilterEnabled,
    dateField,
    dateRange,
    drillDowns,
    isDefaultFiltersDisabled,
    relativeDateRange,
    report,
    scope,
    selectedQuickFilters,
    variableFilters,
    weekStartsOnOverride,
  ]);

  return { settings, w, h };
};

export default useLiveGadgetCopySettings;
